import {
  ICellRendererParams,
  ColDef,
  ValueGetterParams
} from "ag-grid-enterprise";
import {
  numberCellEditorParams,
  numberFilterParams
} from "components/cell-editor/numberCellEditor";

import {
  ActionsCellRenderer,
  bidPackageDueDateHeaderComponent,
  CellValueRendererWithEditIcon,
  dateCellRenderer,
  dueDateCellRenderer,
  nextDeadlineCellRenderer,
  riskCellRenderer,
  specSectionLabel
} from "components/cell-renders";
import {
  twoRowGetQuickFilterText,
  twoRowCellRenderer,
  ArrayStringGetQuickFilterText,
  submittalStatusCellRendererV2,
  FeatureMaterialLinksCellRenderer
} from "entity-app/shared-components/log-render-components/cell-renders";

import {
  coldefNavigation,
  coldefSingleField,
  coldefHiddenField,
  coldefDateField,
  coldefLinkedActivities,
  coldefUser
} from "entity-app/shared-components/log-render-components/col-defs";
import { TBidPackageLog } from "pages/preconstruction-log-page/model";
import { useCallback, useContext, useEffect, useMemo, useRef } from "react";
import featureDateBlockColumns from "entity-app/shared-components/entity-milestones-duration-columns/feature-date-block-columns";
import {
  isPermissionNotGrantted,
  ProjectContext
} from "context/ProjectProvider";
import { UtilColumnConfig } from "components/column-config-views/column-config-util";
import { MUTATION_SAVE_COLUMNS } from "services/graphQL/mutations";
import { useCIQMutation, useCIQQuery } from "hooks/ciq-gql-hooks";
import { getLoggedinUserId } from "services/auth";
import { debounce, upperCase } from "lodash";
import { DateFilter, DateUtils } from "utils/dateutils";
import { FeatureSpecSectionCellEditor } from "components/cell-editor/spec-section-editor";
import SelectCellEditor, {
  SelectCellEditorV2
} from "components/cell-editor/select-cell-editor";
import { submittalSequenceComparator } from "pages/submittals-log-page/utils";
import { FeatureTypes } from "entity-app/constants";
import CustomDateSetFilter from "entity-app/custom-filters/custom-date-filter";
import CustomDateCellEditor from "components/cell-editor/date-cell-editor";
import { backwardCompatibilityForLinkedTasksFilter } from "utils/grid-utils";
import {
  ESubmittalStatus,
  ProjectPermissionEnum,
  SubmittalStatusToStrMap
} from "../../../constants";
import { QUERY_GET_SUBMITTAL_COLUMN_CONFIG } from "../submittal-queries";

const generateSavedColConfigsForColIds = (
  colIds: string[],
  order: number,
  {
    milestonesColumnVisibility,
    durationsColumnVisibility
  }: { milestonesColumnVisibility: boolean; durationsColumnVisibility: boolean }
) => {
  const configStructure: any = {
    field: {
      name: "",
      range: null,
      filter: null,
      agFilter: null,
      sortOrder: null
    },
    order: null,
    visibility: true
  };

  return colIds.map((colId, index) => {
    return {
      field: {
        ...configStructure.field,
        name: colId
      },
      order: order + index + 1,
      visibility: colId.includes("milestone_")
        ? milestonesColumnVisibility
        : durationsColumnVisibility
    };
  });
};

function useSubmittalLogGridConfig(
  projectId: string,
  gridRef: React.RefObject<any>,
  maxNumberOfMilestones: {
    milestonesCount: number;
    lastMilestoneHasOffset: boolean;
  } | null
) {
  const {
    gqlClientForProject,
    columnHeaders: { submittalHeaderMap },
    projectDetails,
    tokenContents
  } = useContext(ProjectContext);

  const [saveColumnsStateMutation] = useCIQMutation(MUTATION_SAVE_COLUMNS, {
    client: gqlClientForProject
  });

  const loggedinUserId = useMemo(() => getLoggedinUserId(), []);
  const { data: columnConfigData } = useCIQQuery<{ project_users: Array<any> }>(
    QUERY_GET_SUBMITTAL_COLUMN_CONFIG,
    {
      fetchPolicy: "no-cache",
      client: gqlClientForProject,
      skip: !gqlClientForProject || !loggedinUserId,
      variables: {
        where: { user_id: { _eq: loggedinUserId } }
      }
    }
  );

  const isInlineEditSubmittalPermissionGranted = useMemo(() => {
    if (!tokenContents?.role) return false;
    return !isPermissionNotGrantted(
      ProjectPermissionEnum.InlineEditSubmittal,
      tokenContents?.role
    );
  }, [tokenContents?.role]);

  const isVoidSubmittal = useCallback((params: any) => {
    const submittalWFStatus = upperCase(
      params?.data?.workflow_status?.code || ""
    );
    return submittalWFStatus === ESubmittalStatus[ESubmittalStatus.VOID];
  }, []);

  const isSubmittalWFNotStarted = useCallback((params: any) => {
    const submittalWFStatus = upperCase(
      params?.data?.workflow_status?.code || ""
    );
    return submittalWFStatus === ESubmittalStatus[ESubmittalStatus.CREATE];
  }, []);

  const isSubmittalWFCompleted = useCallback((params: any) => {
    const submittalWFStatus = upperCase(
      params?.data?.workflow_status?.code || ""
    );
    return submittalWFStatus === ESubmittalStatus[ESubmittalStatus.DONE];
  }, []);

  const isGridCellEditableForBasicFields = useCallback(
    (params: any) => {
      const isDeletedSub = params?.data?.deleted_from_source;
      return (
        isInlineEditSubmittalPermissionGranted &&
        !isVoidSubmittal(params) &&
        !params.context.isIntegrationMode &&
        !isDeletedSub
      );
    },
    [isInlineEditSubmittalPermissionGranted, isVoidSubmittal]
  );

  const isGridCellEditableForWorkflowFields = useCallback(
    (params: any) => {
      const ignoreIntegrationModeColIds = ["planned_float"];
      const shouldIgnoreIntegrationMode = ignoreIntegrationModeColIds.includes(
        params?.colDef?.colId
      );
      const isDeletedSub = params?.data?.deleted_from_source;

      return (
        isInlineEditSubmittalPermissionGranted &&
        isSubmittalWFNotStarted(params) &&
        !isVoidSubmittal(params) &&
        (shouldIgnoreIntegrationMode || !params.context.isIntegrationMode) &&
        !isDeletedSub
      );
    },
    [
      isInlineEditSubmittalPermissionGranted,
      isVoidSubmittal,
      isSubmittalWFNotStarted
    ]
  );

  const isGridCellEditableForWorkflowFieldsNotClosed = useCallback(
    (params: any) => {
      const ignoreIntegrationModeColIds = [
        "planned_float",
        "manual_entry_date"
      ];
      const shouldIgnoreIntegrationMode = ignoreIntegrationModeColIds.includes(
        params?.colDef?.colId
      );
      const isDeletedSub = params?.data?.deleted_from_source;

      return (
        isInlineEditSubmittalPermissionGranted &&
        !isSubmittalWFCompleted(params) &&
        !isVoidSubmittal(params) &&
        (shouldIgnoreIntegrationMode || !params.context.isIntegrationMode) &&
        !isDeletedSub
      );
    },
    [
      isInlineEditSubmittalPermissionGranted,
      isVoidSubmittal,
      isSubmittalWFCompleted
    ]
  );

  const dateBlockColumnsDefs = useMemo(
    () =>
      featureDateBlockColumns(
        maxNumberOfMilestones?.milestonesCount || null,
        maxNumberOfMilestones?.lastMilestoneHasOffset || false
      ),
    [maxNumberOfMilestones]
  );

  const onEditCell = useCallback(
    (params: any) => {
      gridRef?.current?.api?.setFocusedCell(params.rowIndex, params.colKey);
      gridRef?.current?.api?.startEditingCell({
        rowIndex: params.rowIndex,
        colKey: params.colKey
      });
    },
    [gridRef]
  );

  const gridDefaultColDef = useMemo(
    () =>
      ({
        sortable: true,
        editable: false,
        filter: true,
        resizable: true,
        menuTabs: ["filterMenuTab"],
        minWidth: 100,
        cellRenderer: (params: ICellRendererParams) => {
          return CellValueRendererWithEditIcon(params, false);
        },
        cellEditorPopup: false
      } as ColDef<TBidPackageLog>),
    []
  );

  const gridColumnDefs = useMemo(() => {
    return [
      coldefNavigation(
        {
          field: "sequence_id",
          colId: "submittal_sequence_id",
          headerName: submittalHeaderMap.submittal_id,
          pinned: "left",
          lockPosition: "left",
          initialWidth: 80,
          lockVisible: true,
          filter: false,
          sortable: true,
          menuTabs: [],
          editable: false,
          valueGetter: (params: ValueGetterParams) => {
            const { context, data } = params;
            if (!data.sequence_id) return "";
            const value = data.sequence_id || "";
            const cellText =
              context.projectDetails?.spec_section &&
              data.spec_section.length > 0
                ? `${data.spec_section[0].number} - ${data?.sequence_id}`
                : `${value}`;
            return cellText;
          },
          comparator: (valueA: any, valueB: any, nodeA: any, nodeB: any) =>
            submittalSequenceComparator(
              nodeA.data.sequence_id,
              nodeB.data.sequence_id,
              nodeA,
              nodeB,
              projectDetails?.spec_section
            )
        },
        projectId,
        "submittals"
      ),
      coldefSingleField({
        field: "number",
        colId: "submittal_number",
        headerName: submittalHeaderMap?.submittal_number,
        pinned: "left",
        lockPosition: "left",
        initialWidth: 120,

        editable: isGridCellEditableForBasicFields,
        cellRenderer: CellValueRendererWithEditIcon,
        cellClass: (params: any) => {
          return isGridCellEditableForBasicFields(params)
            ? "cell-editable"
            : "";
        }
      }),
      coldefSingleField({
        field: "title",
        colId: "title",
        headerName: submittalHeaderMap.title,
        filter: false,
        sortable: true,
        menuTabs: [],
        initialWidth: 180,
        pinned: "left",
        lockPosition: "left",
        cellRenderer: twoRowCellRenderer,
        cellRendererParams: {
          featureKey: "submittals",
          projectId
        },
        getQuickFilterText: twoRowGetQuickFilterText,
        lockVisible: true,
        cellClass: (params: any) => {
          return isGridCellEditableForBasicFields(params)
            ? "cell-editable"
            : "";
        },
        comparator(valueA: any, valueB: any, nodeA: any, nodeB: any) {
          const compareA = nodeA.data.title ?? nodeA.data.description;
          const compareB = nodeB.data.title ?? nodeB.data.description;
          return compareA.toLowerCase().localeCompare(compareB.toLowerCase());
        },
        editable: isGridCellEditableForBasicFields
      }),
      coldefHiddenField({
        field: "description",
        headerName: "Description",
        pinned: "left",
        lockPosition: "left"
      }),
      coldefSingleField({
        field: "workflow_status",
        colId: "status",
        headerName: submittalHeaderMap?.workflow_status,
        lockVisible: true,
        initialWidth: 200,
        minWidth: 200,
        tooltipField: undefined,
        tooltipValueGetter: undefined,
        cellRenderer: submittalStatusCellRendererV2,
        valueGetter: (params: ValueGetterParams) => {
          return SubmittalStatusToStrMap[params.data.workflow_status.id];
        }
      }),
      coldefDateField({
        field: "planned_next_milestone_date",
        headerName: submittalHeaderMap?.planned_next_milestone_date,
        initialWidth: 160,
        valueGetter: (params: ValueGetterParams) => {
          return DateUtils.format(
            params.data.date_block?.planned_next_milestone_date
          );
        },
        cellRenderer: (params: ICellRendererParams) => {
          return nextDeadlineCellRenderer(params, false);
        }
      }),
      coldefDateField({
        field: "projected_next_milestone_date",
        headerName: submittalHeaderMap?.projected_next_milestone_date,
        initialWidth: 160,
        valueGetter: (params: ValueGetterParams) => {
          return DateUtils.format(
            params.data.date_block?.projected_next_milestone_date
          );
        },
        cellRenderer: (params: ICellRendererParams) => {
          return nextDeadlineCellRenderer(params, false);
        }
      }),
      coldefDateField({
        field: "next_planned_deadline",
        colId: "next_planned_deadline",
        headerName: submittalHeaderMap?.next_planned_deadline || "",
        headerTooltip: submittalHeaderMap?.next_planned_deadline || "",
        cellRenderer: (params: ICellRendererParams) => {
          return nextDeadlineCellRenderer(params, false);
        }
      }),
      coldefSingleField({
        field: "next_action",
        colId: "next_action",
        headerName: submittalHeaderMap?.next_action || "",
        initialWidth: 180,
        editable: false,
        filter: false
      }),
      coldefDateField({
        colId: "manual_entry_date",
        field: "manual_entry_date",
        headerName: submittalHeaderMap?.manual_entry_date || "Deadline Date",
        editable: true,
        cellRenderer: CellValueRendererWithEditIcon,
        cellEditor: CustomDateCellEditor,
        valueGetter: undefined,
        valueFormatter: ({ value }) => (value ? DateUtils.format(value) : ""),
        cellEditorParams: {
          placeholder: `Select ${
            submittalHeaderMap?.manual_entry_date || "Deadline Date"
          }`
        },
        cellClass: (params: any) => {
          return isGridCellEditableForWorkflowFieldsNotClosed(params)
            ? "cell-editable"
            : "";
        }
      }),
      coldefDateField({
        field: "date_block.final_deadline_date",
        colId: "final_deadline_date",
        headerName: submittalHeaderMap?.final_deadline_date || "Deadline",
        headerTooltip: submittalHeaderMap?.final_deadline_date || "Deadline",
        valueGetter: (params: ValueGetterParams) => {
          return DateUtils.format(params.data.date_block?.final_deadline_date);
        },
        cellRenderer: (params: ICellRendererParams) => {
          return nextDeadlineCellRenderer(params, false);
        }
      }),
      coldefDateField({
        field: "date_block.projected_last_milestone_date",
        colId: "projected_last_milestone_date",
        headerName:
          submittalHeaderMap?.projected_last_milestone_date ||
          "Projected Completion Date",
        headerTooltip:
          submittalHeaderMap?.projected_last_milestone_date ||
          "Projected Completion Date",
        valueGetter: (params: ValueGetterParams) => {
          return DateUtils.format(
            params.data.date_block?.projected_last_milestone_date
          );
        },
        cellRenderer: (params: ICellRendererParams) => {
          return nextDeadlineCellRenderer(params, false);
        }
      }),
      coldefDateField({
        field: "due_date",
        colId: "due_date",
        headerName: submittalHeaderMap?.due_date || "",
        headerTooltip: submittalHeaderMap?.due_date || "",
        cellRenderer: (params: ICellRendererParams) => {
          return dueDateCellRenderer(params, false);
        },
        headerComponentParams: bidPackageDueDateHeaderComponent
      }),
      coldefSingleField({
        field: "planned_float",
        colId: "planned_float",
        headerName: "Submittal Planned Float",
        headerTooltip: "Submittal Planned Float",
        initialWidth: 130,
        editable: isGridCellEditableForWorkflowFields,
        cellEditor: "agNumberCellEditor",
        cellEditorParams: numberCellEditorParams,
        filter: "agNumberColumnFilter",
        filterParams: numberFilterParams,
        cellClass: (params: any) => {
          return isGridCellEditableForWorkflowFields(params)
            ? "cell-editable"
            : "";
        }
      }),
      coldefSingleField({
        field: "effective_float",
        colId: "effective_float",
        headerName: "Submittal Actual Float",
        headerTooltip: "Submittal Actual Float",
        initialWidth: 130,
        filter: "agNumberColumnFilter",
        filterParams: numberFilterParams,
        editable: false
      }),
      coldefSingleField({
        field: "spec_section",
        colId: "custom_spec_section",
        headerName: submittalHeaderMap?.spec_no,
        headerTooltip: submittalHeaderMap?.spec_no,
        getQuickFilterText: ArrayStringGetQuickFilterText,
        valueGetter: ({ data }) => {
          if (!data || !data.spec_section || !data.spec_section.length)
            return "";
          const spec = data.spec_section[0];
          const specLabel = specSectionLabel({
            specNumber: spec?.number,
            specName: spec?.name
          });

          return specLabel;
        },
        tooltipField: undefined,
        editable: isGridCellEditableForBasicFields,
        cellEditor: FeatureSpecSectionCellEditor,
        initialWidth: 300,
        minWidth: 300,
        cellClass: (params: any) => {
          return isGridCellEditableForBasicFields(params)
            ? "cell-editable"
            : "";
        }
      }),
      coldefSingleField({
        field: "revision",
        colId: "revision",
        headerName: submittalHeaderMap?.revision || "",
        headerTooltip: submittalHeaderMap?.revision || "",
        tooltipField: undefined,
        editable: false,
        menuTabs: ["filterMenuTab"],
        initialWidth: 150,
        minWidth: 100,
        lockVisible: true
      }),
      coldefSingleField({
        field: "type.value",
        colId: "type",
        headerName: submittalHeaderMap?.type || "",
        headerTooltip: submittalHeaderMap?.type || "",
        tooltipField: undefined,
        editable: isGridCellEditableForBasicFields,
        menuTabs: ["filterMenuTab"],
        initialWidth: 150,
        minWidth: 100,
        cellEditor: SelectCellEditorV2,
        lockVisible: true,
        cellEditorParams: {
          type: "regular-select",
          dataMapVar: "typesMap",
          valueVar: "id",
          labelVar: "value",
          showSearch: true
        },
        cellClass: (params: any) => {
          return isGridCellEditableForBasicFields(params)
            ? "cell-editable"
            : "";
        }
      }),
      coldefSingleField({
        field: "state.code",
        colId: "state",
        headerName: submittalHeaderMap?.state || "",
        headerTooltip: submittalHeaderMap?.state || "",
        tooltipField: undefined,
        editable: false,
        menuTabs: ["filterMenuTab"],
        initialWidth: 150,
        minWidth: 100
      }),
      coldefUser({
        def: {
          field: "assignee_user",
          colId: "assignee",
          headerName: submittalHeaderMap?.assignee,
          editable: false,
          initialWidth: 180
        },
        user: {
          id: "assignee_user.id",
          firstName: "assignee_user.first_name",
          lastName: "assignee_user.last_name",
          statusId: "assignee_user.status_id",
          companyObjField: "assignee_user.company"
        }
      }),

      coldefUser({
        def: {
          field: "gc_user",
          colId: "gc_reviewer",
          headerName: submittalHeaderMap?.gc_reviewer,
          editable: isGridCellEditableForWorkflowFields,
          initialWidth: 180,
          cellClass: (params: any) => {
            return isGridCellEditableForWorkflowFields(params)
              ? "cell-editable"
              : "";
          }
        },
        user: {
          id: "gc_user.id",
          firstName: "gc_user.first_name",
          lastName: "gc_user.last_name",
          statusId: "gc_user.status_id",
          companyObjField: "gc_user.company"
        }
      }),

      coldefUser({
        def: {
          field: "design_reviewer",
          colId: "design_reviewer",
          headerName: submittalHeaderMap?.design_reviewer,
          editable: isGridCellEditableForWorkflowFields,
          initialWidth: 180,
          cellClass: (params: any) => {
            return isGridCellEditableForWorkflowFields(params)
              ? "cell-editable"
              : "";
          }
        },
        user: {
          id: "design_reviewer.id",
          firstName: "design_reviewer.first_name",
          lastName: "design_reviewer.last_name",
          statusId: "design_reviewer.status_id",
          companyObjField: "design_reviewer.company",
          dropdownType: "DR_dropdown"
        }
      }),
      coldefUser({
        def: {
          field: "responsible_contractor",
          colId: "responsible_contractor",
          headerName: submittalHeaderMap?.responsible_contractor,
          editable: isGridCellEditableForWorkflowFields,
          initialWidth: 220,
          cellClass: (params: any) => {
            return isGridCellEditableForWorkflowFields(params)
              ? "cell-editable"
              : "";
          }
        },
        user: {
          id: "responsible_contractor.id",
          firstName: "responsible_contractor.name",
          lastName: "",
          dropdownType: "RC_dropdown",
          participantsSource: "responsibleContractors"
        }
      }),

      coldefUser({
        def: {
          field: "submitter_reviewer",
          colId: "submitter",
          headerName: submittalHeaderMap?.submitter,
          editable: isGridCellEditableForWorkflowFields,
          initialWidth: 180,
          cellClass: (params: any) => {
            return isGridCellEditableForWorkflowFields(params)
              ? "cell-editable"
              : "";
          }
        },
        user: {
          id: "submitter_reviewer.id",
          firstName: "submitter_reviewer.first_name",
          lastName: "submitter_reviewer.last_name",
          statusId: "submitter_reviewer.status_id",
          companyObjField: "submitter_reviewer.company",
          dependentId: "responsible_contractor.id",
          dependentNotFoundContent: "First select a Responsible Contractor",
          dropdownType: "RC_ASSIGNEE_dropdown"
        }
      }),
      coldefLinkedActivities({
        field: "tasks",
        colId: "linked_tasks",
        headerName: submittalHeaderMap?.linked_activities || "",
        headerTooltip: submittalHeaderMap?.linked_activities || ""
      }),
      coldefSingleField({
        field: "linked_items",
        colId: "linked_materials",
        headerName: submittalHeaderMap?.linked_materials || "",
        headerTooltip: submittalHeaderMap?.linked_materials || "",
        cellRenderer: FeatureMaterialLinksCellRenderer,
        valueGetter: ({ data }: any) => {
          return data?.linked_items
            .filter(
              (item: any) =>
                item.feature_type === FeatureTypes.MATERIALS && !item.implicit
            )
            ?.map((material: any) => {
              return material;
            });
        },
        filterParams: {
          valueFormatter: ({ value }: any) => {
            return value ? "Materials Linked" : "No Materials Linked";
          },
          keyCreator: ({ value }: any) => {
            return value ? "Materials Linked" : "No Materials Linked";
          }
        }
      }),
      {
        colId: "action",
        field: "submittal_schedule_links",
        headerName: "Actions",
        headerTooltip: "Actions",
        pinned: "right",
        initialWidth: 100,
        filter: false,
        menuTabs: [],
        cellRenderer: ActionsCellRenderer,
        suppressColumnsToolPanel: true,
        getQuickFilterText: () => ""
      },
      coldefSingleField({
        field: "risk_level",
        colId: "risk_level",
        headerName: submittalHeaderMap?.risk_level,
        initialWidth: 130,
        cellRenderer: (params: ICellRendererParams) => {
          return riskCellRenderer(params, false);
        }
      }),

      coldefSingleField({
        field: "priority",
        colId: "priority",
        headerName: submittalHeaderMap?.priority || "",
        headerTooltip: submittalHeaderMap?.priority || "",
        tooltipField: undefined,
        editable: isGridCellEditableForBasicFields,
        menuTabs: ["filterMenuTab"],
        initialWidth: 150,
        minWidth: 100,
        cellEditor: SelectCellEditor,
        cellEditorParams: {
          type: "regular-select",
          dataMapVar: "priorities",
          valueVar: "value",
          labelVar: "label",
          showSearch: false,
          isSorting: false
        },
        cellClass: (params: any) => {
          return isGridCellEditableForBasicFields(params)
            ? "cell-editable"
            : "";
        }
      }),
      coldefSingleField({
        field: "watchers",
        colId: "watchers",
        headerName: submittalHeaderMap?.watchers,
        initialWidth: 130,
        editable: isGridCellEditableForBasicFields,
        cellClass: (params: any) => {
          return isGridCellEditableForBasicFields(params)
            ? "cell-editable"
            : "";
        }
      }),
      ...dateBlockColumnsDefs.groupCols,
      ...dateBlockColumnsDefs.columnDefsForDateBlock,

      {
        colId: "planned_submittal_distributed_date",
        field: "planned_submittal_distributed_date",
        headerName: "Submittal Distributed Date (Planned)",
        headerTooltip: "Submittal Distributed Date (Planned)",
        hide: true,
        suppressColumnsToolPanel: true,
        initialWidth: 150,
        minWidth: 70,
        maxWidth: 250,
        editable: false,
        cellRenderer: dateCellRenderer,
        comparator: DateFilter.comparator,
        filter: CustomDateSetFilter,
        getQuickFilterText: () => ""
      },
      {
        colId: "planned_trade_partner_submitted_date",
        field: "planned_trade_partner_submitted_date",
        headerName: "Submitted By SUBCONTRACTOR (Planned)",
        headerTooltip: "Submitted By SUBCONTRACTOR (Planned)",
        initialWidth: 150,
        minWidth: 70,
        maxWidth: 350,
        editable: false,
        hide: true,
        suppressColumnsToolPanel: true,
        cellRenderer: dateCellRenderer,
        comparator: DateFilter.comparator,
        filter: CustomDateSetFilter,
        getQuickFilterText: () => ""
      },
      {
        colId: "planned_ext_review_completed_date",
        field: "planned_ext_review_completed_date",
        headerName: "External Review Complete (Planned)",
        headerTooltip: "External Review Complete (Planned)",
        initialWidth: 150,
        minWidth: 70,
        maxWidth: 300,
        editable: false,
        hide: true,
        suppressColumnsToolPanel: true,
        cellRenderer: dateCellRenderer,
        comparator: DateFilter.comparator,
        filter: CustomDateSetFilter,
        getQuickFilterText: () => ""
      },
      {
        colId: "submittal_created_by",
        headerName: "Created By",
        headerTooltip: "Created By",
        hide: true,
        suppressColumnsToolPanel: true,
        initialWidth: 150,
        minWidth: 70,
        maxWidth: 300,
        editable: false,
        valueGetter: ({ data }: any) => {
          try {
            return `${data.created_by_first_name} ${data.created_by_last_name}`;
          } catch (ex) {
            return "";
          }
        },
        getQuickFilterText: () => ""
      },
      {
        colId: "created_at",
        field: "created_at",
        headerName: submittalHeaderMap?.created_at || "",
        headerTooltip: submittalHeaderMap?.created_at || "",
        initialWidth: 200,
        minWidth: 30,
        maxWidth: 220,
        editable: false,
        cellEditorPopup: false,
        comparator: DateFilter.comparator,
        filter: CustomDateSetFilter,
        getQuickFilterText: () => "",
        tooltipValueGetter: (params: any) => {
          return DateUtils.format(params.value);
        },
        tooltipField: "created_at",
        suppressColumnsToolPanel: true,
        hide: true
      }
    ];
  }, [
    dateBlockColumnsDefs.columnDefsForDateBlock,
    dateBlockColumnsDefs.groupCols,
    isGridCellEditableForBasicFields,
    isGridCellEditableForWorkflowFields,
    isGridCellEditableForWorkflowFieldsNotClosed,
    projectDetails?.spec_section,
    projectId,
    submittalHeaderMap
  ]);

  const applyColumnConfig = useCallback(
    ({
      type,
      columnConfig
    }: {
      type: "default" | "custom";
      columnConfig?: any;
    }) => {
      if (!gridRef.current) return;

      const colConfig =
        type === "default"
          ? columnConfigData?.project_users[0]?.submittal_column_config
          : columnConfig;

      const dbColumnIDs = dateBlockColumnsDefs?.columnDefsForDateBlock?.map(
        (col: any) => col.colId
      );

      if (colConfig?.columnList?.length) {
        let sortedColumnConfig = colConfig.columnList.sort(
          (a: any, b: any) => a.order - b.order
        );

        const lastConfigItem =
          sortedColumnConfig[sortedColumnConfig.length - 1];
        const lastConfigItemOrder = lastConfigItem.order;

        const milestonesColumnVisibility = sortedColumnConfig.find(
          (col: any) => col.field.name === "_milestones"
        ).visibility;

        const durationsColumnVisibility = sortedColumnConfig.find(
          (col: any) => col.field.name === "_durations"
        ).visibility;

        const remainingDateBlockColumnConfig = generateSavedColConfigsForColIds(
          dbColumnIDs,
          lastConfigItemOrder,
          {
            milestonesColumnVisibility,
            durationsColumnVisibility
          }
        );

        sortedColumnConfig =
          backwardCompatibilityForLinkedTasksFilter(sortedColumnConfig);

        UtilColumnConfig.setFilterStateFromDataForSubmittalLog({
          columnStates: {
            columnList: [
              ...sortedColumnConfig,
              ...remainingDateBlockColumnConfig
            ],
            quickSearch: ""
          },
          gridRef: {
            api: gridRef.current!.api
          }
        });
      } else {
        gridRef.current!.api.applyColumnState({
          state: [
            { colId: "next_planned_deadline", sort: "asc", sortIndex: 1 }
          ],
          defaultState: { sort: null }
        });
      }
    },
    [
      columnConfigData?.project_users,
      dateBlockColumnsDefs?.columnDefsForDateBlock,
      gridRef
    ]
  );

  const setColumnVisibility = useCallback(
    (column: string[], visible: boolean) => {
      gridRef?.current?.api?.setColumnsVisible(column, visible);
    },
    [gridRef]
  );

  const saveColumnState = useCallback(async () => {
    if (!gridRef.current) return;
    const columnsStates = UtilColumnConfig.getColumnStateFromGrid({
      gridRef: {
        api: gridRef.current!.api
      }
    });

    try {
      const variables = {
        submittal_column_config: columnsStates
      };

      await saveColumnsStateMutation({
        variables
      });
    } catch (ex) {
      console.log("An error occured");
    }
  }, [gridRef, saveColumnsStateMutation]);

  const saveColumnStateRef = useRef(saveColumnState);
  useEffect(() => {
    saveColumnStateRef.current = saveColumnState;
  }, [saveColumnState]);
  const debouncedSaveColumnState = useMemo(
    () =>
      debounce(() => {
        saveColumnStateRef.current();
      }, 1000),
    []
  );

  return {
    gridDefaultColDef,
    gridColumnDefs,
    dateBlockColumnsDefs,
    setColumnVisibility,
    saveColumnState: debouncedSaveColumnState,
    applyColumnConfig,
    onEditCell
  };
}

export default useSubmittalLogGridConfig;
